/**
 * Build and parse raw connector DSL (recursive fields tree).
 */

import { buildInitialFieldsFromManifest, PATH_PARTY_BY_TARGET } from './fieldCatalogUtils';
import { coerceLiteral } from './literalCoercion';

/** Placeholder until the user maps a real source array path in the builder. */
export const PLACEHOLDER_ARRAY_SOURCE = '$.__line_items__';

export function createEmptyScalarMapping() {
  return {
    source: '',
    readAsString: false,
    pipeline: [],
    conditions: [],
    fallback: '',
    defaultValue: undefined,
    useDefaultOnly: false,
  };
}

export function createEmptyArrayTarget() {
  return {
    source: '',
    filter: null,
    items: {},
  };
}

export function rawConfigToBuilderState(rawConfig) {
  if (!rawConfig || typeof rawConfig !== 'object') {
    return {
      target: '',
      scalarMappings: {},
      arrayMappings: {},
    };
  }

  const scalarMappings = {};
  const targets = rawConfig.targets || {};

  Object.entries(targets).forEach(([path, spec]) => {
    scalarMappings[path] = fieldSpecToMapping(spec);
  });

  const arrayMappings = {};
  const arrayTargets = rawConfig.arrayTargets || {};

  Object.entries(arrayTargets).forEach(([name, spec]) => {
    const items = {};
    Object.entries(spec.items || {}).forEach(([itemPath, itemSpec]) => {
      items[itemPath] = fieldSpecToMapping(itemSpec);
    });

    arrayMappings[name] = {
      source: spec.source || '',
      filter: spec.filter || null,
      items,
    };
  });

  return {
    target: rawConfig.target || '',
    scalarMappings,
    arrayMappings,
  };
}

function fieldSpecToMapping(spec) {
  if (!spec || typeof spec !== 'object') {
    return createEmptyScalarMapping();
  }

  const hasSource = Boolean(spec.source);
  const hasDefault = Object.prototype.hasOwnProperty.call(spec, 'default');

  return {
    source: spec.source || '',
    readAsString: spec.readAs === 'string',
    pipeline: Array.isArray(spec.pipeline) ? spec.pipeline.map((s) => ({ ...s })) : [],
    conditions: Array.isArray(spec.conditions) ? spec.conditions.map((c) => ({ ...c })) : [],
    fallback: spec.fallback !== undefined ? String(spec.fallback) : '',
    defaultValue: hasDefault ? spec.default : undefined,
    useDefaultOnly: !hasSource && hasDefault,
  };
}

function applyDefaultToSpec(spec, mapping, fieldPath = '') {
  if (mapping.defaultValue !== undefined && mapping.defaultValue !== null) {
    spec.default = coerceLiteral(mapping.defaultValue, fieldPath);

    return;
  }

  if (mapping.useDefaultOnly) {
    spec.default = '';
  }
}

export function mappingToFieldSpec(mapping, fieldPath = '') {
  if (!mapping) {
    return {};
  }

  const spec = {};

  if (mapping.useDefaultOnly) {
    applyDefaultToSpec(spec, mapping, fieldPath);

    if (mapping.pipeline?.length) {
      spec.pipeline = mapping.pipeline;
    }

    if (mapping.conditions?.length) {
      spec.conditions = mapping.conditions;
    }

    if (mapping.fallback !== '' && mapping.fallback !== undefined) {
      spec.fallback = coerceLiteral(mapping.fallback, fieldPath);
    }

    return spec;
  }

  if (mapping.source) {
    spec.source = mapping.source;
  }

  if (mapping.readAsString) {
    spec.readAs = 'string';
  }

  if (mapping.pipeline?.length) {
    spec.pipeline = mapping.pipeline;
  }

  if (mapping.conditions?.length) {
    spec.conditions = mapping.conditions;
  }

  if (mapping.fallback !== '' && mapping.fallback !== undefined) {
    spec.fallback = coerceLiteral(mapping.fallback, fieldPath);
  }

  applyDefaultToSpec(spec, mapping, fieldPath);

  return spec;
}

/**
 * Ensures compiled raw config is non-empty (merge placeholders from manifest / prior save).
 */
export function ensurePersistableRawConfig(rawConfig, { manifest, fallbackRawConfig } = {}) {
  const result = {
    ...rawConfig,
    targets: { ...(rawConfig?.targets || {}) },
  };

  if (Object.keys(result.targets).length === 0) {
    const fallbackTargets = fallbackRawConfig?.targets || {};

    for (const path of manifest?.scalarPaths || Object.keys(fallbackTargets)) {
      result.targets[path] = fallbackTargets[path] || { default: '' };
    }
  }

  const arrayTargets = { ...(rawConfig?.arrayTargets || {}) };
  const fallbackArrays = fallbackRawConfig?.arrayTargets || {};
  const manifestArrays = manifest?.arrayTargets || {};

  for (const arrayName of new Set([...Object.keys(manifestArrays), ...Object.keys(fallbackArrays)])) {
    if (arrayTargets[arrayName]?.source && Object.keys(arrayTargets[arrayName]?.items || {}).length > 0) {
      continue;
    }

    const fallbackEntry = fallbackArrays[arrayName];
    const itemPaths = manifestArrays[arrayName] || Object.keys(fallbackEntry?.items || {});

    if (!itemPaths.length && !fallbackEntry) {
      continue;
    }

    const items = { ...(arrayTargets[arrayName]?.items || {}) };
    itemPaths.forEach((itemPath) => {
      if (!items[itemPath] || Object.keys(items[itemPath]).length === 0) {
        items[itemPath] = fallbackEntry?.items?.[itemPath] || { default: '' };
      }
    });

    arrayTargets[arrayName] = {
      source: arrayTargets[arrayName]?.source || fallbackEntry?.source || PLACEHOLDER_ARRAY_SOURCE,
      items,
      ...(fallbackEntry?.filter ? { filter: fallbackEntry.filter } : {}),
    };
  }

  if (Object.keys(arrayTargets).length > 0) {
    result.arrayTargets = arrayTargets;
  }

  return result;
}

export function ensurePathPartyScaffold(fields, target) {
  const partyKey = PATH_PARTY_BY_TARGET[target];
  if (!partyKey || !fields || typeof fields !== 'object') {
    return;
  }

  if (!fields[partyKey]) {
    fields[partyKey] = {
      fields: {
        profile_id: { default: '' },
        tin: { default: '' },
      },
    };

    return;
  }

  const party = fields[partyKey];
  if (!party.fields || typeof party.fields !== 'object') {
    party.fields = {};
  }

  if (!party.fields.profile_id) {
    party.fields.profile_id = { default: '' };
  }

  if (!party.fields.tin) {
    party.fields.tin = { default: '' };
  }
}

/**
 * Minimal valid raw config for version create — satisfies compile validation.
 */
export function buildInitialRawConfig({ target, manifest, mappingId }) {
  const fields = buildInitialFieldsFromManifest(manifest);

  if (Object.keys(fields).length === 0) {
    throw new Error('Cannot create version without required-field schema for this document type');
  }

  ensurePathPartyScaffold(fields, target);

  const rawConfig = { target, fields };

  if (mappingId) {
    rawConfig.mappingId = mappingId;
  }

  return rawConfig;
}

export function builderStateToRawConfig({ target, scalarMappings, arrayMappings, mappingId }) {
  const targets = {};

  Object.entries(scalarMappings || {}).forEach(([path, mapping]) => {
    const spec = mappingToFieldSpec(mapping, path);
    if (Object.keys(spec).length > 0) {
      targets[path] = spec;
    }
  });

  const arrayTargets = {};

  Object.entries(arrayMappings || {}).forEach(([name, arraySpec]) => {
    const items = {};
    Object.entries(arraySpec.items || {}).forEach(([itemPath, mapping]) => {
      const spec = mappingToFieldSpec(mapping, itemPath);
      if (Object.keys(spec).length > 0) {
        items[itemPath] = spec;
      }
    });

    if (Object.keys(items).length === 0 || !arraySpec?.source) {
      return;
    }

    const entry = { source: arraySpec.source, items };

    if (arraySpec.filter) {
      entry.filter = arraySpec.filter;
    }

    arrayTargets[name] = entry;
  });

  const rawConfig = {
    target,
    targets,
  };

  if (mappingId) {
    rawConfig.mappingId = mappingId;
  }

  if (Object.keys(arrayTargets).length > 0) {
    rawConfig.arrayTargets = arrayTargets;
  }

  return rawConfig;
}

/**
 * Format raw_config for the JSON editor textarea.
 */
export function formatRawConfigForEditor(rawConfig) {
  if (!rawConfig || typeof rawConfig !== 'object') {
    return '{\n  "fields": {}\n}';
  }

  return JSON.stringify(rawConfig, null, 2);
}

function sourceToSpreadsheetColumn(source) {
  if (typeof source !== 'string') {
    return '';
  }

  const trimmed = source.trim();
  if (!trimmed.startsWith('$.')) {
    return '';
  }

  const column = trimmed.slice(2);
  if (!column || column.includes('.')) {
    return '';
  }

  return column;
}

function readFieldSource(fields, partyKey, leafKey) {
  if (!fields || typeof fields !== 'object') {
    return '';
  }

  const party = fields[partyKey];
  if (!party || typeof party !== 'object' || !party.fields) {
    return '';
  }

  const leaf = party.fields[leafKey];
  if (!leaf || typeof leaf !== 'object' || typeof leaf.source !== 'string') {
    return '';
  }

  return leaf.source.trim();
}

/** Derive SFTP path profile spreadsheet columns from path-party field mappings. */
export function readPathPartyColumnsFromRaw(rawConfig) {
  if (!rawConfig || typeof rawConfig !== 'object') {
    return { profileSource: '', tinSource: '', profileColumn: '', tinColumn: '' };
  }

  const target = rawConfig.target || '';
  const partyKey = PATH_PARTY_BY_TARGET[target];
  if (!partyKey) {
    return { profileSource: '', tinSource: '', profileColumn: '', tinColumn: '' };
  }

  const profileSource = readFieldSource(rawConfig.fields, partyKey, 'profile_id');
  const tinSource = readFieldSource(rawConfig.fields, partyKey, 'tin');

  return {
    profileSource,
    tinSource,
    profileColumn: sourceToSpreadsheetColumn(profileSource),
    tinColumn: sourceToSpreadsheetColumn(tinSource),
  };
}

/**
 * Normalize pasted raw config before save — metadata only, no manifest merge.
 */
export function normalizeRawConfigForSave(parsed, { connector } = {}) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Config must be a JSON object');
  }

  const result = { ...parsed };

  if (!result.target && connector?.document_type) {
    result.target = connector.document_type;
  }

  if (!result.mappingId && connector?.key) {
    result.mappingId = connector.key;
  }

  if (!result.fields || typeof result.fields !== 'object' || Array.isArray(result.fields)) {
    throw new Error('Config must include a "fields" object');
  }

  return result;
}

export function initScalarMappingsFromManifest(manifest, existingMappings = {}) {
  const scalarMappings = { ...existingMappings };
  const scalarPaths = manifest?.scalarPaths || [];

  scalarPaths.forEach((path) => {
    if (!scalarMappings[path]) {
      scalarMappings[path] = createEmptyScalarMapping();
    }
  });

  return scalarMappings;
}

export function initArrayMappingsFromManifest(manifest, existingMappings = {}) {
  const arrayMappings = { ...existingMappings };
  const arrayTargets = manifest?.arrayTargets || {};

  Object.entries(arrayTargets).forEach(([arrayName, itemPaths]) => {
    if (!arrayMappings[arrayName]) {
      arrayMappings[arrayName] = createEmptyArrayTarget();
    }

    const items = { ...arrayMappings[arrayName].items };
    itemPaths.forEach((itemPath) => {
      if (!items[itemPath]) {
        items[itemPath] = createEmptyScalarMapping();
      }
    });
    arrayMappings[arrayName] = { ...arrayMappings[arrayName], items };
  });

  return arrayMappings;
}
